/** Raw PUT uploads to presigned URLs with progress (XMLHttpRequest — fetch has no upload progress). */

export class UploadError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

export function putFile(
  url: string,
  file: Blob,
  headers: Record<string, string>,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) {
      // Browsers refuse to set these; the request carries them anyway.
      if (/^(content-length|host)$/i.test(k)) continue;
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve();
      } else reject(new UploadError(xhr.status, `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new UploadError(0, 'Network error during upload'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Client errors other than timeouts / throttling are not worth retrying. */
function retryable(e: unknown): boolean {
  if (e instanceof UploadError) return e.status === 0 || e.status === 408 || e.status === 429 || e.status >= 500;
  return !(e instanceof DOMException && e.name === 'AbortError');
}

/** PUT with up to `retries` retries and exponential backoff (0.8s, 1.6s, 3.2s…). */
export async function putWithRetry(
  url: string,
  file: Blob,
  headers: Record<string, string>,
  onProgress: (fraction: number) => void,
  { retries = 3, baseDelay = 800, signal }: { retries?: number; baseDelay?: number; signal?: AbortSignal } = {},
): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    try {
      await putFile(url, file, headers, onProgress, signal);
      return;
    } catch (e) {
      if (attempt >= retries || !retryable(e)) throw e;
      onProgress(0);
      await sleep(baseDelay * 2 ** attempt);
    }
  }
}
